import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import { withOptimizely, type WithOptimizelyProps } from '@optimizely/react-sdk';
import { generateClassName } from '@klv/shared/utils/functions';
import { Wrapper } from '@klv/utils/wrapper';
import pricingData from '@klv/shared/pricing/static/us.json';
import sendsData from '@klv/shared/pricing/static/sends.json';
import cdpPricing from '@klv/shared/pricing/static/cdp.json';
import reviewsPricing from '@klv/shared/pricing/static/reviews.json';
import marketingAnalyticsPricing from '@klv/shared/pricing/static/analytics.json';

import customerHubPricing from '@klv/shared/pricing/static/customerHub.json';
import aiConversationsPricing from '@klv/shared/pricing/static/aiConversations.json';
import helpdeskTicketsPricing from '@klv/shared/pricing/static/helpdeskTickets.json';
import Slider from '@klv/molecules/slider';
import Icon from '@klv/atoms/icon';
import staticData from './stories/data';
import PricingCalculator from './pricing-calculator';
import { calculateRequiredCreditTier, getPriceForCreditTier } from './utils/pricing';
import MaxContactsElement from './max-contacts';
import PlatformCard from './platform-card';
import PlanCard from './plan-card';

import * as styles from './styles/pricingV3.module.css';
import type { IPricingV3, IEmailPlan, IFormattedData, IMessagingType } from './types';
import type { TCreditType } from './pricing-calculator/utils';

type TEmailPlans = keyof typeof pricingData['email'];



export const PricingV3 = withOptimizely(({
  className = '',
  cardOrder,
  MaxContacts,
  cardContent,
  platformCards,
  excludeFreeCard = false,
  hidePlatformCards = false,
  optimizely,
  optimizelyReadyTimeout,
  ...props
}: IPricingV3 & WithOptimizelyProps) => {
  const { t } = useTranslation();

  // Default values for pricing sliders
  const DEFAULT_CONTACTS = 1001;
  const DEFAULT_SMS_CREDITS = 1250;
  const DEFAULT_MESSAGES = 250;

  const getInitialValues = () => {
    if (typeof window !== 'undefined') {
      const initialParams = new URLSearchParams(window.location.search);
      const paramContacts = Number(initialParams.get('contacts'));
      const paramMessages = Number(initialParams.get('messages'));

      // Use URL values if they exist, otherwise use defaults
      const initialContacts = paramContacts || DEFAULT_CONTACTS;
      const initialMessages = paramMessages || DEFAULT_MESSAGES;

      return { initialContacts, initialMessages };
    }
    return { initialContacts: DEFAULT_CONTACTS, initialSmsCredits: DEFAULT_SMS_CREDITS, initialMessages: DEFAULT_MESSAGES };
  };

  const { initialContacts, initialMessages } = getInitialValues();

  const [contacts, setContacts] = useState<number>(initialContacts);
  const [messages, setMessages] = useState<number>(initialMessages);


  // Update URL when slider values change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.hash === '#servicePlans') {
        const el = document.getElementById('servicePlans');
        const y = el ? el.getBoundingClientRect().top + window.scrollY - 80 : 0;
        window.scrollTo({ top: y, behavior: 'smooth' });

      }
      url.searchParams.set('contacts', contacts.toString());
      url.searchParams.set('messages', messages.toString());
      window.history.replaceState({}, '', url);
    }
  }, [contacts, messages]);
  
  const getDefaultLocale = () => {
    const pathPrefix = t('langCodes_pathPrefix');
    return pathPrefix === "" ? "US" : pathPrefix.toUpperCase();
  };
  
  const [selectedLocale, setSelectedLocale] = useState<string>(getDefaultLocale());
  const [showMaxContacts, setShowMaxContacts] = useState<boolean>(false);
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);

  const [messagingType, setMessagingType] = useState<IMessagingType['type']>('sms');

  const handleToggleChange = (type: IMessagingType['type']) => {
    setMessagingType(type);
  };

  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleAllExpanded = () => {
    setIsAllExpanded((prev) => !prev);
    setTimeout(() => {
      buttonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  };

  const { email, emailAndSms, free } = cardContent || {};

  const emailPlans = pricingData.email;

  const selectedPlan: IEmailPlan = emailPlans[contacts.toString() as TEmailPlans];


  useEffect(() => {
    if (selectedPlan?.min_customers > 1 && showMaxContacts) {
      setShowMaxContacts(false);
    }
  }, [selectedPlan, showMaxContacts]);

  useEffect(() => {
    optimizely?.track?.('client_pageview');
  }, [optimizely]);

  const formatData = <T extends object>(
    data: Record<string, T>,
    labelFn: (item: T) => string,
    extraFieldsFn?: (item: T) => object, // Optional function to add extra fields like price
  ): IFormattedData[] => {
    return Object.entries(data).map(([key, item]) => ({
      value: parseInt(key, 10),
      label: labelFn(item),
      ...(extraFieldsFn ? extraFieldsFn(item) : {}), // Add extra fields if provided
    }));
  };

  const emailData = formatData(pricingData.email, (item) =>
    item.max_customers === -1
      ? '250,000+'
      : `${item.min_customers.toLocaleString()} – ${item.max_customers.toLocaleString()}`,
  );
  const analyticsData = formatData(
    marketingAnalyticsPricing.analytics,
    (item) => item.max_customers === -1
      ? `${item.min_customers.toLocaleString()}+`
      : `${item.min_customers.toLocaleString()} – ${item.max_customers.toLocaleString()}`,
    (item) => ({ price: item.price }),
  );
  const customerHubData = formatData(
    customerHubPricing.customerHub,
    (item) => item.max_customers === -1
      ? `${item.min_customers.toLocaleString()}+`
      : `${item.min_customers.toLocaleString()} – ${item.max_customers.toLocaleString()}`,
    (item) => ({ price: item.price, fullPrice: item.fullPrice }),
  );

  const messagesData = formatData(sendsData.messages, (item) => `${item.send_limit.toLocaleString()}`);
  const cdpData = formatData(cdpPricing.cdp, (item) => `${item.profiles.toLocaleString()}`);
  const reviewsData = formatData(reviewsPricing.reviews, (item) => `${item.monthly_orders.toLocaleString()}`);
  const customerAgentData = formatData(aiConversationsPricing.aiConversations, (item) => `${item.aiConversations.toLocaleString()}`);
  const helpdeskData = formatData(helpdeskTicketsPricing.helpdeskTickets, (item) => `${item.tickets.toLocaleString()}`);

const FreeCard = () => (
    <PlanCard
      className={styles.styledPlanCard}
      buttonLink={free?.buttons?.[0]?.url || t('organisms_navigation_dataActions_signUp_button_href')}
      buttonOpenInNewTab={free?.buttons?.[0]?.openInNewTab}
      buttonText={free?.buttons?.[0]?.text || t('organisms_pricing_signUp')}
      contacts={contacts}
      description={free?.description as string}
      id="pricing--plan-card--free"
      emailSends={500}
      name={t('organisms_pricing_plans_free_name')}
      price={0}
      messagingType={messagingType === 'mms' ? 'mms' : 'sms'}
      selectedLocale={selectedLocale}
      setSelectedLocale={setSelectedLocale}
      featuresList={free?.featuresList}
      isAllExpanded={isAllExpanded}
      setShowMaxContacts={setShowMaxContacts}
    />
  );

  const EmailCard = () => (
    <PlanCard
      className={styles.styledPlanCard}
      buttonLink={email?.buttons?.[0]?.url || t('organisms_navigation_dataActions_signUp_button_href')}
      buttonOpenInNewTab={email?.buttons?.[0]?.openInNewTab}
      buttonText={email?.buttons?.[0]?.text || t('organisms_pricing_signUp')}
      contacts={contacts}
      description={email?.description as string}
      id="pricing--plan-card--email"
      emailSends={selectedPlan?.send_limit}
      name={t('organisms_pricing_plans_email_name')}
      price={selectedPlan?.price}
      selectedLocale={selectedLocale}
      setSelectedLocale={setSelectedLocale}
      featuresList={email?.featuresList}
      isAllExpanded={isAllExpanded}
      setShowMaxContacts={setShowMaxContacts}
    />
  );

  // Calculate effective credits for messaging
  const effectiveCredits = useMemo(() => {
    const result = calculateRequiredCreditTier(
      messages, 
      selectedLocale, 
      messagingType as TCreditType
    );
    return result;
  }, [messages, selectedLocale, messagingType]);

  const messagingPrice = getPriceForCreditTier(effectiveCredits);

  const EmailAndSMSCard = () => (
    <PlanCard
      className={styles.styledPlanCard}
      credits={effectiveCredits}
      messagingType={messagingType}
      selectedLocale={selectedLocale}
      setSelectedLocale={setSelectedLocale}
      buttonLink={emailAndSms?.buttons?.[0]?.url || t('organisms_navigation_dataActions_signUp_button_href')}
      buttonOpenInNewTab={emailAndSms?.buttons?.[0]?.openInNewTab}
      buttonText={emailAndSms?.buttons?.[0]?.text || t('organisms_pricing_signUp')}
      contacts={contacts}
      description={emailAndSms?.description as string}
      id="pricing--plan-card--email-and-sms"
      emailSends={selectedPlan?.send_limit}
      name={t('organisms_pricing_plans_emailPlusMobileMessages_name')}
      price={selectedPlan?.price + messagingPrice}
      featuresList={emailAndSms?.featuresList}
      isAllExpanded={isAllExpanded}
      setShowMaxContacts={setShowMaxContacts}
    />
  );

  const adpCard = useMemo(() => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={cdpData}
      name={t('organisms_pricing_plans_advanced_data_platform_headline')}
      description={platformCards?.customerDataPlatform.description}
      featuresList={platformCards?.customerDataPlatform.featuresList}
      subText={t('organisms_pricing_plans_platform_advanced_data_platform_subText')}
      subTextInfoHeader={t('organisms_pricing_plans_platform_advanced_data_platform_subTextInfoHeader')}
      subTextInfo={t('organisms_pricing_plans_advance_data_platform_subTextTooltiptInfo')}
      pricingDescription={t('organisms_pricing_plans_advanced_data_platform_pricingDescription')}
    />
  ), [cdpData, platformCards, t]);

  const renderedRewiewsCard = useMemo(() => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={reviewsData}
      name="Reviews"
      description={platformCards?.reviews.description}
      featuresList={platformCards?.reviews.featuresList}
      subText={t('organisms_pricing_plans_reviews_slider_subText')}
      pricingDescription={t('organisms_pricing_plans_reviews_subText')}
    />
  ), [reviewsData, platformCards, t]);

  const MarketingAnalytics = () => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={analyticsData}
      name={t('organisms_pricing_plans_marketing_analytics_headline')}
      description={platformCards?.marketingAnalytics?.description}
      featuresList={platformCards?.marketingAnalytics.featuresList}
      subText={t('organisms_pricing_plans_marketing_analytics_price_subText')}
      pricingDescription={t('organisms_pricing_plans_marketing_analytics_subText')}
      activeProfiles={contacts}

    />
  );

  const CustomerHub = () => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={customerHubData}
      name={t('organisms_pricing_plans_customer_hub_headline')}
      description={platformCards?.customerHub?.description}
      featuresList={platformCards?.customerHub.featuresList}
      pillText={platformCards?.customerHub?.pillText}
      cardButtonText={platformCards?.customerHub.cardButtonText}
      cardButtonUrl={platformCards?.customerHub.cardButtonUrl}
      subText={t('organisms_pricing_plans_customer_hub_price_subText')}
      pricingDescription={t('organisms_pricing_plans_customer_hub_pricingDescription')}
      activeProfiles={contacts}
      showDiscount
    />
  );

  const customerAgentCard = useMemo(() => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={customerAgentData}
      name="Customer Agent"
      description={platformCards?.customerAgent?.description}
      featuresList={platformCards?.customerAgent?.featuresList}
      pillText={platformCards?.customerAgent?.pillText}
      cardButtonText={platformCards?.customerAgent?.cardButtonText}
      cardButtonUrl={platformCards?.customerAgent?.cardButtonUrl}
      subText={t('organisms_pricing_plans_customer_agent_slider_subText')}
      subTextInfo={t('organisms_pricing_plans_customer_agent_subTextInfo')}
      subTextInfoHeader={t('organisms_pricing_plans_customer_agent_subTextInfoHeader')}
      pricingDescription={t('organisms_pricing_plans_customer_agent_pricingDescription')}
      showDiscount
    />
  ), [customerAgentData, platformCards, t]);

  const helpdeskCard = useMemo(() => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={helpdeskData}
      name={t('organisms_pricing_plans_helpdesk_headline')}
      description={platformCards?.helpdesk?.description}
      featuresList={platformCards?.helpdesk?.featuresList}
      pillText={platformCards?.helpdesk?.pillText}
      cardButtonText={platformCards?.helpdesk?.cardButtonText}
      cardButtonUrl={platformCards?.helpdesk?.cardButtonUrl}
      subText={t('organisms_pricing_plans_helpdesk_slider_subText')}
      subTextInfo={t('organisms_pricing_plans_helpdesk_subTextInfo')}
      subTextInfoHeader={t('organisms_pricing_plans_helpdesk_subTextInfoHeader')}
      pricingDescription={t('organisms_pricing_plans_helpdesk_pricingDescription')}
      showDiscount
    />
  ), [helpdeskData, platformCards, t]);

  const getMobileCardOrder = () => {
    const cardLayouts = [
      [EmailAndSMSCard, EmailCard, FreeCard],      
      [EmailAndSMSCard, FreeCard, EmailCard],
      [EmailCard, EmailAndSMSCard, FreeCard], 
      [EmailCard, FreeCard, EmailAndSMSCard],
      [FreeCard, EmailAndSMSCard, EmailCard], 
      [FreeCard, EmailCard, EmailAndSMSCard], 
    ];
    return cardLayouts[cardOrder || 0];
  };

  const [LeadCard, MiddleCard, LastCard] = getMobileCardOrder();

  return (
    <div className={generateClassName([className, styles.pricingV3])} {...props}>
      <div className="grid">
        <div className={styles.contactsSlider}>
          <Slider
            headline={t('organisms_pricing_contacts_slider_headline')}
            subText={t('organisms_pricing_contacts_slider_subText')}
            subTextInfo={t('organisms_pricing_contacts_slider_subTextInfo')}
            subTextInfoHeader={t('organisms_pricing_contacts_slider_subText')}
            range={emailData}
            value={contacts}
            onChange={setContacts}
          />
        </div>
        <div className={styles.messagesSlider}>
          <Slider
            headline={t('organisms_pricing_messages_slider_headline')}
            subText={t('organisms_pricing_messages_slider_subText')}
            subTextInfo={t('organisms_pricing_messages_slider_subTextInfo')}
            subTextInfoHeader={t('organisms_pricing_messages_slider_subText')}
            italicizedSubText={t('organisms_pricing_messages_slider_microSubText')}
            range={messagesData}
            value={messages}
            onChange={setMessages}
          />
        </div>
        <PricingCalculator
          backgroundColor="light"
          messages={messages}
          selectedLocale={selectedLocale}
          onToggleChange={handleToggleChange}
          onLocaleChange={setSelectedLocale}
        />
      </div>

      {showMaxContacts ? (
        <MaxContactsElement mixedMedia={staticData.mixedMedia} />
      ) : (
        <div>
          <div className={generateClassName(['grid', styles.desktopPlans, excludeFreeCard ? styles.centeredPlans : ''])}>
            {excludeFreeCard ? [
              (<><EmailAndSMSCard /><EmailCard /></>),
              (<><EmailAndSMSCard /><EmailCard /></>),
              (<><EmailCard /><EmailAndSMSCard /></>),
              (<><EmailCard /><EmailAndSMSCard /></>),
              (<><EmailAndSMSCard /><EmailCard /></>),
              (<><EmailCard /><EmailAndSMSCard /></>),
            ][cardOrder || 0] : [
              (<><EmailAndSMSCard /><EmailCard /><FreeCard /></>),
              (<><EmailAndSMSCard /><FreeCard /><EmailCard /></>),
              (<><EmailCard /><EmailAndSMSCard /><FreeCard /></>),
              (<><EmailCard /><FreeCard /><EmailAndSMSCard /></>),
              (<><FreeCard /><EmailAndSMSCard /><EmailCard /></>),
              (<><FreeCard /><EmailCard /><EmailAndSMSCard /></>),
            ][cardOrder || 0]}
          </div>

          {/* Mobile layout */}
          <div className={styles.mobilePlans}>
            <LeadCard />
            <div 
              className={styles.mobileDisclaimer}
              dangerouslySetInnerHTML={{ __html: t('organisms_pricing_legal_copy') }}
            />
            <MiddleCard />
            <LastCard />
          </div>
          <div 
            className={styles.disclaimer}
            dangerouslySetInnerHTML={{ __html: t('organisms_pricing_legal_copy') }}
          />
          <div className={styles.buttonContainer}>
            <button ref={buttonRef} onClick={toggleAllExpanded}>
              {isAllExpanded ? (
                <div className={styles.collapsible}>
                  <div>{t('organisms_pricing_plans_collapsible_button')}</div>
                  <Icon className={styles.iconUp} iconName="ChevronDown" />
                </div>
              ) : (
                <div className={styles.collapsible}>
                  <div>{t('organisms_pricing_plans_expandable_button')}</div>
                  <Icon className={styles.iconDown} iconName="ChevronDown" />
                </div>
              )}
            </button>
          </div>
        </div>
      )}

      {!hidePlatformCards && (
        <>
          <Wrapper
            backgroundColor={platformCards?.backgroundColor}
            customPadding={{ customPaddingBottom: platformCards?.customPadding?.customPaddingTop, customPaddingTop: platformCards?.customPadding?.customPaddingBottom }}
          >
            <div id="platformCards" className={styles.platformCards}>
              <div className={styles.platformCardsTitle}>
                {t('organisms_pricing_plans_platform_headline')}
              </div>
              <div className={styles.serviceSubHeadline}>
                {t('organisms_pricing_plan_marketing_subHeadline')}
              </div>
              <div className={`grid ${styles.plans}`}>
                {renderedRewiewsCard}
                <MarketingAnalytics />
                {adpCard}
              </div>
            </div>
          </Wrapper>
          {platformCards?.includeServiceCards && (
            <Wrapper
            backgroundColor={platformCards?.backgroundColor}
            customPadding={{ customPaddingBottom: platformCards?.customPadding?.customPaddingTop, customPaddingTop: platformCards?.customPadding?.customPaddingBottom }}
          >
            <div className={generateClassName([styles.platformCards, styles.servicePlans])} id="servicePlans">
              <div className={styles.platformCardsTitle}>
                {t('organisms_pricing_plans_service_headline')}
              </div>
              <div className={styles.serviceSubHeadline}>
                {t('organisms_pricing_plans_service_subHeadline')}
              </div>
              <div className={styles.introPricing}>
                {t('organisms_pricing_plans_service_intro_pricing')}
              </div>
              <div className={`grid ${styles.plans}`}>
                {customerAgentCard}
                <CustomerHub />
                {helpdeskCard}
              </div>
            </div>
          </Wrapper>)}
        </>
      )}
    </div>
  );
});

